import {
  chaosCountForLevel,
  flippingDensityForLevel,
  gridSizeForLevel,
  inversionCountForLevel,
  lockedDensityForLevel,
  mirrorDensityForLevel,
  moveBudgetForLevel,
  penaltyDensityForLevel,
  rotationDensityForLevel,
} from './levels.js';
import { paletteForLevel } from './palette.js';
import type { Cell, Board, Coord, GameState, TileType } from './types.js';
import { coordKey } from './types.js';
import { isCornerConnected } from './region.js';
import { minStepsToWin } from './solver.js';
import { createRng, type Rng } from './rng.js';

const pickCorners = (size: number, level: number, rng: Rng): { start: Coord; end: Coord } => {
  if (level <= 5) return { start: { x: 0, y: 0 }, end: { x: size - 1, y: size - 1 } };
  const corners: { start: Coord; end: Coord }[] = [
    { start: { x: 0, y: 0 }, end: { x: size - 1, y: size - 1 } },
    { start: { x: size - 1, y: 0 }, end: { x: 0, y: size - 1 } },
    { start: { x: 0, y: size - 1 }, end: { x: size - 1, y: 0 } },
    { start: { x: size - 1, y: size - 1 }, end: { x: 0, y: 0 } },
  ];
  return rng.pick(corners);
};

const pickTile = (rng: Rng, level: number): TileType => {
  const lockedP = lockedDensityForLevel(level);
  const penaltyP = penaltyDensityForLevel(level);
  const flipP = flippingDensityForLevel(level);
  const rotP = rotationDensityForLevel(level);
  const mirrorP = mirrorDensityForLevel(level);
  if (rng.chance(lockedP)) return 'locked';
  if (rng.chance(penaltyP)) return 'penalty';
  if (rng.chance(flipP)) return 'flipping';
  if (rng.chance(rotP)) return rng.chance(0.5) ? 'rotateL' : 'rotateR';
  if (rng.chance(mirrorP)) return rng.chance(0.5) ? 'mirrorH' : 'mirrorV';
  return 'normal';
};

const sprinkleTile = (
  cells: Cell[][],
  rng: Rng,
  count: number,
  tile: TileType,
  protectedKeys: Set<string>,
): void => {
  let need = count;
  if (need <= 0) return;
  const size = cells.length;
  let safety = 200;
  while (need > 0 && safety-- > 0) {
    const x = rng.int(size);
    const y = rng.int(size);
    if (protectedKeys.has(coordKey({ x, y }))) continue;
    const cur = cells[y]![x]!;
    if (cur.tile !== 'normal') continue;
    cells[y]![x] = { color: cur.color, tile };
    need--;
  }
};

const buildCells = (
  size: number,
  palette: readonly string[],
  rng: Rng,
  start: Coord,
  end: Coord,
  level: number,
): Cell[][] => {
  const protectedKeys = new Set([coordKey(start), coordKey(end)]);
  const cells: Cell[][] = [];
  for (let y = 0; y < size; y++) {
    const row: Cell[] = [];
    for (let x = 0; x < size; x++) {
      const isProtected = protectedKeys.has(coordKey({ x, y }));
      const tile: TileType = isProtected ? 'normal' : pickTile(rng, level);
      const color = rng.pick(palette);
      if (tile === 'flipping') {
        let alt = rng.pick(palette);
        let attempts = 0;
        while (alt === color && attempts++ < 5) alt = rng.pick(palette);
        row.push({ color, tile, altColor: alt });
      } else {
        row.push({ color, tile });
      }
    }
    cells.push(row);
  }
  sprinkleTile(cells, rng, inversionCountForLevel(level), 'inversion', protectedKeys);
  sprinkleTile(cells, rng, chaosCountForLevel(level), 'chaos', protectedKeys);
  return cells;
};

const ensureSeed = (seed: number, level: number): { board: Board; min: number } | null => {
  const rng = createRng(seed * 9301 + level * 49297 + 233280);
  const size = gridSizeForLevel(level);
  const palette = paletteForLevel(level);
  const { start, end } = pickCorners(size, level, rng);
  const cells = buildCells(size, palette, rng, start, end, level);
  const board: Board = { size, cells, start, end, palette, level };
  if (isCornerConnected(board)) return null;
  const min = minStepsToWin(board);
  if (min === null || min < 1) return null;
  return { board, min };
};

export const generateLevel = (level: number, baseSeed: number): GameState => {
  for (let attempt = 0; attempt < 60; attempt++) {
    const candidate = ensureSeed(baseSeed + attempt, level);
    if (!candidate) continue;
    const { board, min } = candidate;
    return {
      board,
      movesUsed: 0,
      moveBudget: moveBudgetForLevel(level, min),
      status: 'playing',
      visitedPenalty: new Set(),
      visitedSpecial: new Set(),
    };
  }
  throw new Error(`generator: no solvable level for ${level} (base seed ${baseSeed})`);
};
